import { TabbedLayoutPageList } from '../../../backbone-unorganized/models';
import { Account, FormStructure, SearchCriteria, SearchCriterias } from '../../../backbone-unorganized/shared/forms';
import { MobileTask, MobileTasks } from '../../../backbone-unorganized/tasks/models';
import { BluetoothErrorCode, getErrorMessageFromCode } from '../../../backbone-unorganized/utils/bluetooth';
import { FormTemplatesPage, TasksPage } from '../../../backbone-unorganized/views';
import { ZenputCodes } from '../../../constants';
import FormReorderingPage from '../../../react/features/FormReordering';
import { BackboneProvider } from '../../../react/features/providers/BackboneProvider';
import { FollowUpActions } from '../../../react/features/FollowUpActions';
import { CordovaPlugins } from '../../../shared/CordovaPlugins';
import { CommentsPageLayoutMixin } from '../../shared/models/CommentsPageLayoutMixin';
import { BaseToolbarNavPage, BaseToolbarNavPageLayout, EmptyView } from '../../shared/views/baseLayoutViews';
import { EmptyListMessage } from '../../shared/views/EmptyListMessage';
import { MobileSearchBar } from '../../shared/views/MobileSearchBar';
import { ReactPageWrapper } from '../../shared/views/ReactPageWrapper';
import { BaseTabbedLayoutPage, TabbedLayout } from '../../shared/views/tabs';
import { AccountFieldView } from './accountField/views';
import { BarcodeFieldView } from './barcodeField/views';
import { CheckboxFieldView } from './checkboxField/views';
import { DatetimeFieldView } from './datetimeField/views';
import { DocumentFieldView } from './documentField/views';
import { EmailFieldView } from './emailField/views';
import { FormTableOfContents } from './FormTableOfContents/views';
import { FormulaFieldView } from './formulaField/views';
import { ImageFieldView } from './imageField/views';
import { IngredientEquipmentTemperatureFieldView } from './ingredientEquipmentTemperatureFields/views';
import { InstructionsFieldView } from './instructionsField/views';
import { MobileSubmission, MobileSubmissions } from './MobileFormStructure/models';
import { NumberFieldView } from './numberField/views';
import { OilQualityFieldView } from './oilQualityField/views';
import { RangeFieldView } from './rangeField/views';
import { SectionFieldView, SectionHeaderView } from './sectionField/views';
import { SelectFieldView } from './selectField/views';
import { SensorTemperatureFieldView } from './sensorTemperatureField/views';
import { SignatureFieldView } from './signatureField/views';
import { StopwatchFieldView } from './stopwatchField/views';
import { TemperatureFieldView } from './temperatureField/views';
import { TextFieldView } from './textField/views';
import { VideoFieldView } from './videoField/views';
import { YesNoFieldView } from './yesNoField/views';
import { isNative, isWeb, isConnected, isIos, isWindows, isAndroid } from '../../../shared/utils/is';
import { logMobileUsage } from '../../../shared/utils/mobileUsageLogging';
import {
    actionSheet,
    confirmNotification,
    hideLoadingDialog,
    showOfflineMessage,
    showSuccessMessage,
    showLoadingDialog,
    showErrorMessage,
    toastNotification
} from '../../../shared/utils/notifications';
import { getDebuggingFlag } from '../../../shared/utils/debugging';
import { FollowUpAction, uploadFollowUpActionsFiles } from '../../../backbone-unorganized/followUpActions/models';

/**
 * This is our hacked-together view for a single 'section' of our mobile form. This doesn't use any collection or models directly,
 * but instead is loaded up with instances of BaseFieldView through `add_child_view()`. This allows us to have separate
 * form section views with their own logic but without splitting up our FormStructure collection.
 *
 * @see MobileForm.appendHtml() for an example implementation of this
 *
 * @note This is an ItemView because it technically doesn't use Marionette's Composite-/Collection-View logic
 */

export const FormSection = Backbone.Marionette.ItemView.extend({
    template: '#tpl_form_section',
    itemViewContainer: 'ul.form_container',

    children_views: null, // [] // instead of using a collection, these will just be passed in - BaseFieldView
    is_collapsed: false,
    SLIDE_TIME_MS: 200,
    section_parent: null, // MobileSectionField

    triggers: {
        expanded_noop: 'expanded'
    },

    events: {
        'click .section_header, .section_footer': 'toggle_collapse'
    },

    templateHelpers: function () {
        var helpers = {
            show_section_footer: !!this.section_parent,
            is_collapsed: this.is_collapsed
        };

        // We will only show numbers if we are in a section with a section field in it, so we will just return
        // the standard template helpers if we don't have one.
        if (!this.section_parent) {
            return helpers;
        }

        var section_stats = this.section_parent.get_stats();
        var footer_text = null;
        var is_required_class = null;

        if (
            section_stats.get('num_required_incomplete') === 0 ||
            section_stats.get('num_required') === 0 ||
            this.section_parent.values_exist_in_section() === 0
        ) {
            footer_text = _t('mobile_forms_required_questions_complete');
            is_required_class = 'required_complete';
        } else {
            footer_text = _t('mobile_forms_required_remaining', {
                count: section_stats.get('num_required_incomplete')
            });
            is_required_class = 'required_not_complete';
        }

        // @note to avoid reloading the entire form section, some of these will be manually adjusted in _update_footer()
        return $.extend(helpers, {
            num_total: section_stats.get('num_total'),
            footer_text: footer_text,
            is_required_class: is_required_class
        });
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        this.children_views = []; // defining here prevents pass-by-reference issue
    },

    add_child_view: function (view) {
        var this_ = this;

        this.children_views.push(view);
        this.listenTo(view.model, 'change', function (model) {
            if (
                model.get('type') === 'section' &&
                model.get('metadata') &&
                model.get('metadata').is_collapsed === false
            ) {
                this_.expand();
            }

            this_._update_footer();
        });

        if (view.model.temperature_collection) {
            this.listenTo(view.model.temperature_collection, 'follow_up_actions_on_change', () => {
                this_._update_footer();
            });
        }
        return this;
    },

    onBeforeRender: function () {
        this._add_remove_collapsed_class();
    },

    /**
     * Render the fields that belong in this form section. Because we're not using a collection here,
     * we need to loop through our `children_views` array and render each one.
     */
    onRender: function () {
        var this_ = this;

        if (this.templateHelpers().num_total == 0) {
            this.$el.addClass('section_no_fields');
        }

        $.each(this.children_views, function (i, view) {
            if (view.model === this_.section_parent) {
                // 'section' fields are special and deserve to be put in our header
                this_.$el.prepend(view.$el);
            } else {
                const $root = this_.$(this_.itemViewContainer);
                const fieldSelector = `#field_${view.model.get('id')}`;
                const fieldNode = $root.find(fieldSelector);

                // If the node is already appended, we need to remove it since we are relying on ID's
                if (fieldNode.length) {
                    fieldNode.remove();
                }
                // everything else, show as a normal <li> in our <ul>
                $root.append(view.$el);
            }
        });
    },

    toggle_collapse: function (event) {
        if (this.is_collapsed) {
            this.expand();
        } else {
            this.collapse($(event.target).hasClass('section_footer'));
        }

        return false;
    },

    _slide: function (animation, on_complete) {
        on_complete || (on_complete = function () {});

        this.$el.find('.form_container_wrapper').velocity(animation, {
            duration: this.SLIDE_TIME_MS,
            easing: 'easeInOutCubic',
            complete: on_complete
        });
    },

    expand: function (callback) {
        callback = callback || function () {};
        var this_ = this;

        var _callback = callback;
        callback = function () {
            this_.trigger('expanded');
            _callback();
        };

        if (!this.is_collapsed) {
            callback();
            return;
        }

        this.is_collapsed = false;
        this._add_remove_collapsed_class();
        this._update_section_field_metadata();

        this._slide('slideDown', function () {
            callback();
        });
    },

    collapse: function (adjust_scrollpos) {
        if (this.is_collapsed) {
            return;
        }

        this.is_collapsed = true;
        this._add_remove_collapsed_class();
        this._update_section_field_metadata();

        this._slide('slideUp');

        if (!adjust_scrollpos) {
            return;
        }
        // If the user clicked the footer to close, scroll to the section header to keep up with the slideUp
        // @note we should use Velocity for this, but it doesn't always work correctly
        var $target_el = this.$el.find('.section_header');
        var $page_scrollable = $('.page_scrollable');
        $page_scrollable.animate(
            {
                scrollTop: $target_el.offset().top - $page_scrollable.offset().top + $page_scrollable.scrollTop() - 100
            },
            this.SLIDE_TIME_MS
        );
    },

    _add_remove_collapsed_class: function () {
        if (this.is_collapsed) {
            this.$el.addClass('collapsed');
        } else {
            this.$el.removeClass('collapsed');
        }
    },

    /**
     * Checks to see if there are any `.invalid` fields in the section and, if there are,
     * expands the section if it's closed.
     * @return {boolean} `true` if there are invalid fields, else `false`
     */
    has_invalid_fields: function () {
        return this.$el.find('.invalid').length !== 0;
    },

    _update_section_field_metadata: function () {
        var field = $.grep(this.children_views, function (field_view, i) {
            return field_view.model.get('type') == 'section';
        })[0];

        if (!field.model.get('metadata')) {
            field.model.set('metadata', {}, { silent: true });
        }

        field.model.get('metadata').is_collapsed = this.is_collapsed;
        field.model.trigger('change', field.model); // trigger change so this is cached in local storage
    },

    _update_footer: function () {
        this.$el.find('.section_footer').html(this.templateHelpers().footer_text);
        this.$el
            .find('.section_footer')
            .removeClass('required_not_complete')
            .removeClass('required_complete')
            .addClass(this.templateHelpers().is_required_class);
    }
});

export const MobileFormPage = BaseToolbarNavPageLayout.extend({
    screen_name: 'form_submission',
    template: '#tpl_mobile_form_page',
    tabs: [], // an array of f.mobile.views.TabbedLayout
    active_comment_id: null, // the comment ID to focus, if any
    expanded_metadata: null, // Holds rule information used to display alerts and tasks

    events: {
        'click .view_previous_submissions': 'view_previous_submissions',
        'click .view_reorder_page': 'view_reorder_page'
    },

    regions: {
        r_tabbed_layout: '.mobile_form_tabbed_layout'
    },

    initialize: function () {
        var this_ = this;
        if (!this.collection) {
            throw new Error('Programmer error: MobileForm must have a collection.');
        }

        this.toolbarnav_title = this.collection.metadata.get('title');

        this.toolbarnav_actions = [
            {
                container_class: 'view_previous_submissions',
                icon_class: 'df-history',
                i18n_key_text: 'mobile_mobileform_text'
            }
        ];

        if (this.collection.metadata.get('is_secure')) {
            this.toolbarnav_actions = [
                {
                    display: 'icon',
                    icon_class: 'df-lock',
                    container_class: 'lock_icon'
                }
            ];
        }
        const order_fields_by_location = this.collection.metadata.get('order_fields_by_location');
        const locationField = this.collection.findWhere({ type: 'account' });

        if (order_fields_by_location) {
            this.toolbarnav_actions.push({
                container_class: `view_reorder_page ${!locationField ? 'disabled' : ''}`,
                icon_class: '',
                i18n_key_text: 'mobile_mobileform_reorder_fields'
            });
        }

        this.tabs = [
            {
                id: 'mobile_form_info',
                name: _t('mobile_dashboard_tab_title_info'),
                view_class: MobileForm,
                view_options: { collection: this.collection, expanded_metadata: this.expanded_metadata }
            }
        ];
        this.listenTo($f.application.vent, 'mobile_form:scroll_to_element', function (options) {
            this_.scroll_to_element(options.element, options.speed, options.offset);
        });

        this.listenTo($f.application.vent, 'mobile_form:on_orientation_change', function () {
            this_.on_orientation_change();
        });
        this.listenTo(this.collection, 'reset', this.onShow);
    },

    onShow: function () {
        MobileFormPage.__super__.onShow.apply(this, arguments);

        this._show_tabbed_layout();

        // @author:david We need to re-call on_orientation_change and restore_scroll_position because we've
        // shown our region since onShow originally fired.
        if (this.is_historic_view) {
            $f.mobile.nav.restore_scroll_position();
        }

        this.on_orientation_change();
        this.trigger_dynamic_form_first_load_event();
    },

    /**
     * Trigger setting the visibility of the questions/sections on Dynamic Forms for the first time
     * passing the id of the already selected location, such as in previous submissions.
     */
    trigger_dynamic_form_first_load_event: function () {
        const account_field = this.collection.findWhere({ type: 'account' });
        this.collection.trigger('dynamicform:first_load', account_field ? account_field.get('value') : null);
    },

    /**
     * When the user clicks 'add task', look for an account field in the form and set it as the default value.
     */
    toolbarnav_create_task_options: function () {
        var _account = this.collection.findWhere({ type: 'account' }); // choose the first account field
        if (!_account || _account.get('hidden') || !_account.get('value') || !_account.get('account')) {
            return {};
        }

        return {
            default_account: new Account(_account.get('account'))
        };
    },

    view_previous_submissions: function (event) {
        $f.application.vent.trigger('mobileform:show_history');
        return false;
    },

    view_reorder_page: function (event) {
        event.preventDefault();

        const locationField = this.collection.findWhere({ type: 'account' });
        const locationValue = this.getLocationValue();
        const locationFieldId = locationField && locationField.get('id');
        const locationFieldHasValue = !!locationValue;
        const isOffline = !isConnected();

        const saveSuccess = () => {
            hideLoadingDialog();
            showSuccessMessage();
            this.collection.trigger('reset');
        };
        const saveError = () => {
            hideLoadingDialog();
            showErrorMessage();
        };
        const handleSave = (field_order) => {
            showLoadingDialog();
            $f.mobile.nav.go_back();
            this.collection.locationFieldOrder.save(
                {
                    field_order: {
                        field_order
                    }
                },
                {
                    success: saveSuccess,
                    error: saveError
                },
                {
                    patch: true
                }
            );
        };

        const handleBack = (field_order) => {
            const text = _t('mobile_message_reordering_confirm');

            if (!isWeb()) {
                var options = {
                    title: _t('mobile_forms_leaving_reordering'),
                    subtitle: text,
                    position: [50, 60], // only used for iPad
                    addCancelButtonWithLabel: _t('mobile_action_sheet_cancel')
                };

                if (isIos()) {
                    options.buttonLabels = [_t('mobile_forms_reordering_button_save')];
                    options.addDestructiveButtonWithLabel = _t('mobile_common_button_discard');
                    options.destructiveButtonLast = true;
                } else if (isAndroid()) {
                    options.buttonLabels = [
                        _t('mobile_forms_reordering_button_save'),
                        _t('mobile_common_button_discard')
                    ];
                    options.androidTheme = CordovaPlugins().actionsheet.ANDROID_THEMES.THEME_DEVICE_DEFAULT_LIGHT;
                    options.androidEnableCancelButton = true;
                } else if (isWindows()) {
                    options.buttonLabels = [
                        _t('mobile_forms_reordering_button_save'),
                        _t('mobile_common_button_discard')
                    ];
                }

                actionSheet(options, function (button_index) {
                    if (button_index === 3) {
                        return; // cancel
                    }

                    if (button_index === 2) {
                        $f.mobile.nav.go_back();
                        return;
                    }
                    handleSave(field_order);
                });
                return;
            }

            confirmNotification(
                text,
                function (success) {
                    if (!success) {
                        $f.mobile.nav.go_back();
                        return;
                    }
                    handleSave(field_order);
                },
                _t('mobile_forms_leaving_reordering'),
                _t('mobile_forms_reordering_button_save'),
                _t('mobile_common_button_discard')
            );
        };

        if (isOffline) {
            showOfflineMessage();
        } else if (locationFieldHasValue) {
            $f.mobile.nav.show(ReactPageWrapper, {
                renderFunction: () => (
                    <FormReorderingPage
                        fields={this.collection.serialize_for_reordering()}
                        onGoBack={handleBack}
                        onSave={handleSave}
                        _t={_t}
                    />
                )
            });
        } else {
            this.$(`#field_${locationFieldId}`).addClass('invalid');
        }
    },

    go_back: function (event) {
        $f.application.vent.trigger('mobileform:go_back:save_draft');
    },

    _show_tabbed_layout: function () {
        this._tabbedLayout = new TabbedLayout({
            is_tabs_full_width: true,
            hide_tabs: this.tabs.length === 1,
            tab_pages: new TabbedLayoutPageList(this.tabs),
            active_tab_id: this.active_comment_id ? $f.shared.comments.utils.COMMENTS_TAB_ID : null
        });
        this.r_tabbed_layout.show(this._tabbedLayout);
    },

    getLocationValue: function () {
        const account = this.collection.findWhere({ type: 'account' });
        return account && account.get('value');
    }
});

export const MobileFormFields = Backbone.Marionette.CollectionView.extend({
    collection: null,
    itemViewEventPrefix: 'fieldview',

    _section_views: [], // our sub-views that group together our itemViewws. @see appendHtml()

    // Some fields (e.g., SelectAccountModal) need access to the
    // parent collection (i.e., what used to be active_collection).
    itemViewOptions: function () {
        return {
            form_collection: this.collection
        };
    },

    getItemView: function (item) {
        // NOTE: @author:halah @jira: FORMS-444 block windows from displayiny document fields
        if (item.get('type') === 'document' && isWindows() && isNative()) {
            item.unsupported_field_type = true;
            return EmptyView; // Defaulting to nothing (EmptyView since getItemView cannot return null)
        }

        this.collection.add_item_to_field_map(item);

        switch (item.get('type')) {
            case 'email':
                return EmailFieldView;
            case 'text':
                return TextFieldView;
            case 'number':
                return NumberFieldView;
            case 'stopwatch':
                return StopwatchFieldView;
            case 'formula':
                return FormulaFieldView;
            case 'select':
                return SelectFieldView;
            case 'checkbox':
                return CheckboxFieldView;
            case 'image':
                return ImageFieldView;
            case 'video':
                return VideoFieldView;
            case 'signature':
                return SignatureFieldView;
            case 'section':
                if (this._is_collapsable_section(item)) {
                    return SectionHeaderView;
                }
                return SectionFieldView;
            case 'account':
                return AccountFieldView;
            case 'datetime':
                return DatetimeFieldView;
            case 'barcode':
                return BarcodeFieldView;
            case 'yesno':
                return YesNoFieldView;
            case 'range':
                return RangeFieldView;
            case 'instructions':
                return InstructionsFieldView;
            case 'temperature':
                return TemperatureFieldView;
            case 'oil_quality':
                return OilQualityFieldView;
            case 'ingredient_temperature':
            case 'equipment_temperature':
                return IngredientEquipmentTemperatureFieldView;
            case 'sensor_temperature':
                return SensorTemperatureFieldView;
            case 'document':
                return DocumentFieldView;
            default:
                // NEVER PUT AN ELSE CASE HERE.  Much safer to just ignore the field for backwards compatability
                item.unsupported_field_type = true;
                return EmptyView; // Defaulting to nothing (EmptyView since getItemView cannot return null)
        }
    },

    _is_collapsable_section: function (field) {
        return (
            field.get('type') == 'section' && field.get_dependent_field_level() === $f.utils.DEPENDENT_FIELD_TOP_LEVEL
        );
    },

    /**
     * Rather than separate our Form Structure into multiple collections for each section, we use
     * Backbone.Marionette's `appendHtml()` to separate each field `itemView` into separate section `div`s.
     *
     * @note that we display our 'section' field types outside of the rest of the form's <ul><li> structure.
     *
     * @note It seems that Backbone.Marionette's `appendHtml()` takes advantage of a `elBuffer` to minimize writes
     * to the DOM. However, it doesn't seem to use it in this case, so we can ignore it in our own implementation.
     */
    appendHtml: function (compositeView, itemView) {
        var $last_section = this.$el.find('.form_section:last');

        // If we need a new section, build it. We will always append to the last form_section
        if (!$last_section.length || this._is_collapsable_section(itemView.model)) {
            this.$el.append('<div class="form_section card"></div>');
            $last_section = this.$el.find('.form_section:last');

            this._section_views.push(
                new FormSection({
                    el: $last_section.get(0),
                    $el: $last_section,
                    is_collapsed:
                        itemView.model.get('metadata') &&
                        typeof itemView.model.get('metadata').is_collapsed !== 'undefined'
                            ? itemView.model.get('metadata').is_collapsed
                            : false,
                    section_parent: this._is_collapsable_section(itemView.model) ? itemView.model : null
                })
            );
        }

        this._section_views[this._section_views.length - 1].add_child_view(itemView); // we will render these views in onRender()
    },

    onBeforeRender: function () {
        this._close_section_views();
    },

    onRender: function () {
        // Render our section subviews
        $.each(this._section_views, function (i, view) {
            view.render();
        });
    },

    expand_invalid_sections: function (on_section_expanded) {
        on_section_expanded = on_section_expanded || function () {};

        $.each(this._section_views, function (i, view) {
            if (!view.has_invalid_fields()) {
                return true;
            }

            view.expand(on_section_expanded);
        });
    },

    _close_section_views: function () {
        if (this._section_views.length == 0) {
            return;
        }

        $.each(this._section_views, function (i, view) {
            view.close();
        });
        this._section_views = [];
    },

    onClose: function () {
        this._close_section_views();
    }
});

/**
 *  Item view for showing a rule and its associated alerts and tasks triggered by the submission
 */
export const SubmissionsTaskAlertRule = Backbone.Marionette.ItemView.extend({
    template: '#tpl_submission_tasks_alert_rule'
});

/**
 *  Composite for the rule title and the collection of rules triggered by this submission
 */
export const SubmissionsTasksAlertsRulesCard = Backbone.Marionette.CompositeView.extend({
    template: '#tpl_submission_tasks_alert_rules_card',
    itemView: SubmissionsTaskAlertRule,
    itemViewContainer: '.task_alert_rules',
    collection: null // Generic collection of rules fetched as expanded_metadata.rules
});

/**
 * The body area of the mobile app; the form, itself
 */
export const MobileForm = BaseTabbedLayoutPage.extend({
    template: '#tpl_mobile_form',

    className: function () {
        return `${MobileForm.__super__.className} wrapper`;
    },

    tagName: 'div',

    // setting this to true before opening another app (e.g., contact picker) will prevent us from reloading the active_collection when the user returns:
    _prevent_next_reload_on_resume: false,
    _has_temperature_field: false,
    _field_map: null, // FormStructureFieldMap
    _last_visited_model_id: null, // reference for last touched field
    _total_invalid: 0,

    events: {
        'click .view_previous_submissions': 'view_previous_submissions',
        'click #submit_form': 'click_submit',
        'click .prev': 'click_error_wizard_button',
        'click .next': 'click_error_wizard_button',
        'change .form_field': 'field_changed',
        'click .section_sticky_header': 'show_table_of_contents',
        'click .create_follow_up_action': 'createFollowUpAction'
    },

    regions: {
        r_fields: '.form_outer_container',
        r_submission_tasks_alerts: '.submission_tasks_alerts'
    },

    templateHelpers: function () {
        return {
            metadata: this.collection.metadata,
            smetadata: this.collection.smetadata,
            default_sticky_header_text: '', // we won't show anything by default
            rules: this.expanded_metadata ? this.expanded_metadata.rules : null,
            disable_submit_button: !!window.UNSAVED_CHANGES_FROM_BUILDER,
            submit_button_follow_up_actions: this.collection.metadata.get('allow_follow_up_actions')
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
        var this_ = this;

        $f.window.last_rdbms_id = this.collection.metadata.get('rdbms_id'); // this is hacky way for us to handle the global form for mobile web

        // Get the device's location in the background so we have it cached for the account picker, etc.
        $f.mobile.get_device_location({
            only_if_permission_granted: true, // don't ask for permission since we don't technically need it yet
            cache_allowance_ms: 0 // do not use cache
        });

        this.listenTo($f.application.vent, 'mobileform:go_back:save_draft', function () {
            this_.save_draft(function () {
                $f.mobile.remove_active_collection(function () {
                    $f.mobile.nav.go_back();
                });
            });
        });

        this.listenTo($f.application.vent, 'mobileform:show_history', function () {
            this_._show_history();
        });
    },

    onBeforeRender: function () {
        // If we are re-rendering and the wizard is not open yet, we should not show invalid's yet.
        if (this._isRendered && !this._is_wizard_open) {
            this.collection.each(function (model) {
                model.is_valid = null;
            });
        }
    },

    onRender: function () {
        var this_ = this;

        this.r_fields.close();
        this.r_fields.show(
            new MobileFormFields({
                collection: this.collection
            })
        );
        this._show_submision_tasks_alerts();
        $f.application.vent.trigger('mobile_form:on_orientation_change');

        this.stopListening(this.r_fields.currentView, 'fieldview:mobile_form:render');
        this.listenTo(this.r_fields.currentView, 'fieldview:mobile_form:render', function () {
            this_.render();
        });

        this.stopListening(this.r_fields.currentView, 'fieldview:request_error_wizard_recalculation');
        this.listenTo(this.r_fields.currentView, 'fieldview:request_error_wizard_recalculation', function (view) {
            if (!this_._is_wizard_open) {
                return;
            }
            this_.update_error_wizard(view.model);
        });

        this.stopListening(this.r_fields.currentView, 'fieldview:prevent_next_reload_on_resume');
        this.listenTo(this.r_fields.currentView, 'fieldview:prevent_next_reload_on_resume', function () {
            this_._prevent_next_reload_on_resume = true;
        });

        this._setup_bluetooth(); // will only setup if has temperature field(s) and is not web

        // Handle validation for hidden fields when they hide or show.
        this.stopListening(this.collection, 'field:hidden_field:show');
        this.stopListening(this.collection, 'field:hidden_field:hide');
        this.listenTo(this.collection, 'field:hidden_field:show', function (model) {
            if (this_._is_wizard_open) {
                // if wizard open, we want ot immediately validate empty values
                model.validate();
            } else {
                // otherwise, silently validate without updating the DOM so our is_valid is accurate
                // we do this in a setTimeout, because the field might not be rendered yet
                // and setting is_valid to false might show validation errors when opening
                // the form
                setTimeout(() => {
                    model.is_valid = model.isValid(true, true);
                });
            }
        });
        this.listenTo(this.collection, 'field:hidden_field:hide', function (model) {
            // We don't always re-validate when we submit, so we need to do this here
            // to make sure the hidden field does not prevent submission if invalid.
            model.validate(); // trigger validation to fire
        });

        this.listenTo(this.collection, 'dynamicform:sections_hidden', function () {
            this.$el.removeClass('has_sections');
        });

        this.listenTo(this.collection, 'dynamicform:sections_visible', function () {
            this.$el.addClass('has_sections');
        });

        this.listenTo(
            this.collection,
            'dynamicform:location_attributes_unavailable',
            _.debounce(function () {
                showErrorMessage(_t('mobile_message_error_location_attributes_unavailable'));
            }, 100)
        );

        // Show or hide the sticky header depending on if we are in a section with a title
        if (this.collection.findWhere({ type: 'section' })) {
            this.$el.addClass('has_sections');

            // update sticky headers now and when the user scrolls
            this._update_sticky_section_header();
            this.$el
                .find('.page_scrollable')
                .off('scroll.mobile_form')
                .on('scroll.mobile_form', function () {
                    this_._update_sticky_section_header();
                });
        }

        if (!this._isShown) {
            return;
        }

        this._on_show_render();
    },

    onShow: function () {
        var this_ = this;

        // There's a very strange issue on some devices (iPhone 5) where the form will appear blank. Simply
        // toggling the `display` or `visibility` properties unfucks it. (@bug:MOBILE-139)
        this.$el.find('.form_outer_container').css('visibility', 'hidden');
        setTimeout(function () {
            this_.$el.find('.form_outer_container').css('visibility', '');
        }, 0);

        this._on_show_render();

        if (this.collection.metadata.get('is_secure')) {
            this._display_secure_form_header();
        }
    },

    /**
     * Runs only on show and successive renders (not on render before show)
     *
     * @todo:marionette_showrender @jira:INF-559
     */
    _on_show_render: function () {
        this._update_sticky_section_header();
        this._perform_form_field_events(); // this will occur in onShow if not here

        // Location-based dynamic visibility is applied imperatively to the DOM and does not survive a
        // re-render (e.g. prefill's), so replay it here beside the value-based field events above.
        if ($f.flags.get_flag('fix_dynamic_visibility_prefill')) {
            this.collection.reapply_dynamic_visibility();
        }
    },

    on_resume: function () {
        var this_ = this;
        _.each(this.collection.models, function (model) {
            if (model.get('type') === 'stopwatch') {
                $f.mobile.settings.get('stopwatch_start_date_' + model.id, function (value) {
                    this_.r_fields.currentView.children.findByModel(model).render();
                });
            }
        });

        if (this.collection.is_started() || this._prevent_next_reload_on_resume || $f.window.has_received_zenput_uri) {
            this._setup_bluetooth();
            return;
        }

        $f.mobile.load_collection(this.collection.metadata.get('rdbms_id')); // _setup_bluetooth() will be called in onRender once loaded
        this._prevent_next_reload_on_resume = false;
    },

    _update_sticky_section_header: function () {
        var $current_section = this._get_current_section_el();
        var $sticky_header = this.$el.find('.section_sticky_header');

        // show sticky header with correct text
        var new_text = null;
        if ($current_section) {
            new_text = $current_section.text().trim();
        } else {
            new_text = this.templateHelpers().default_sticky_header_text;
        }

        if ($sticky_header.find('.section_sticky_name').text().trim() !== new_text) {
            $sticky_header.find('.section_sticky_name').html(new_text);
        }
    },

    show_table_of_contents: function (event) {
        var this_ = this;
        var $current_section = this._get_current_section_el();
        var current_section_id = null;

        if ($current_section) {
            current_section_id = parseInt($current_section.attr('id').replace('field_', ''));
        }

        $f.application.bottom_sheet.show(
            new FormTableOfContents({
                collection: this.collection,
                current_section_id: current_section_id,
                on_click_section: function (section) {
                    $f.application.bottom_sheet.close();

                    if (!section) {
                        // if there's no section, assume the user chose Top of Form
                        return this_.scroll_to_element(0);
                    }

                    // Before we scroll to section, ensure it's expanded.
                    // (Section view will pick up on change and expand if necessary.)
                    if (!section.get('metadata')) {
                        section.set('metadata', { is_collapsed: false });
                    } else {
                        section.get('metadata').is_collapsed = false;
                        section.trigger('change', section);
                    }

                    this_.scroll_to_field(section.id, null, -50);
                }
            })
        );

        return false;
    },

    click_submit: function (event) {
        if (event) {
            event.preventDefault();
        }
        // Wait for any update to active_collection to finish
        setTimeout(() => this.submit(event), 300);
    },

    submit: function (event) {
        var this_ = this;
        var $submit_btn = $(event.target);

        if ($submit_btn.hasClass('disabled') || $submit_btn.attr('disabled')) {
            return false;
        }

        // Fix for Android 4.4: Delay for 300 ms before submitting so there is enough time to hide the keyboard
        if (isNative() && $(window).height() < $f.window.viewport.height) {
            setTimeout(function () {
                this_.click_submit(event);
            }, 300);
            return false;
        }

        var running_stopwatches = this.get_running_stopwatches();
        if (running_stopwatches.length) {
            showErrorMessage(_t('mobile_message_error_stopwatch_field_running'));
            this.scroll_to_field(running_stopwatches[0].id);
            return false;
        }

        if (getDebuggingFlag('disable_template_validation') && process.env.DEPLOY_ENV !== 'production') {
            // no validation on activity templates in this case
        } else {
            // VALIDATE!
            if (!this.show_error_wizard()) {
                return false;
            }
        }

        if (!this.collection.is_started()) {
            showErrorMessage(_t('mobile_message_error_fill_out_form'));
            return false;
        }

        this.collection.set_location_order();

        // add date_completed here
        this_.collection.smetadata.set('date_completed', { $date: new Date().getTime() });

        // add time_zone here
        this_.collection.smetadata.set('time_zone', $f.active_user.get('time_zone'));

        var task_details = this_.collection.smetadata.get('task');

        if (!task_details) {
            this_._add_record_and_start_upload();
            return false;
        }

        // If we don't have an account id or an account field in our form don't check if the task matches the account field
        if (task_details.account_id && this_.collection.findWhere({ type: 'account' })) {
            // loop through all account fields and make sure this account_id is in one of them.
            // If not, disassociate the task from the submission.
            var has_task_account_id = false;
            _.each(this_.collection.where({ type: 'account' }), function (model) {
                if (!model.get('account')) {
                    return;
                }
                if (model.get('account').id === task_details.account_id) {
                    has_task_account_id = true;
                }
            });

            if (!has_task_account_id) {
                // unset task, we don't have the account its for
                this_.collection.smetadata.set({
                    task: null,
                    project: null
                });
            }
        }

        // Store task_id if present on submission
        $f.mobile.cache.update_cache_item({
            item_id: task_details.id,
            cache_key_substrs: MobileTasks.cache_blacklist_key,
            update_fn: function (old_item) {
                old_item.current_state = 'processing';
                return old_item;
            },
            callback: function () {
                this_._add_record_and_start_upload();
            }
        });

        // @todo: How to get back failed local storage
        return false;
    },

    /**
     * Adds a record to a LocalRecords instances (e.g., $f.mobile.pending_tasks_in_processing).
     *
     * This wraps LocalRecords.add_record() but uses a promise instead of callbacks. Eventually we'll
     * update add_records() to return a promise, but this helps for now.
     *
     * @param  {LocalRecords}   local_records_instance   The instance of the LocalRecords collection (usually $f.mobile.*)
     * @param  {LocalRecord}    record                   The model to add as a record.
     *
     */
    _add_to_local_records: (local_records_instance, record) => {
        return new Promise((resolve, reject) => {
            local_records_instance.add_records(record, resolve, reject);
        });
    },

    addFollowUpActionsToSubmissionRecord: (collection) => {
        return new Promise((resolve, reject) => {
            $f.mobile.pending_form_submissions.add_record(collection, resolve, reject);
        });
    },

    _add_record_and_start_upload: function () {
        // add the record and start uploading
        // Log added for @jira:ZP-3651
        this._disable_submit_btn();
        $f.mobile.pending_form_submissions.add_record(
            this.collection,
            () => {
                $f.window.last_rdbms_id = this.collection.metadata.get('rdbms_id');

                var has_task = Boolean(this.collection.smetadata.get('task')?.id);

                // Proceeds to the next page
                var _proceed = async () => {
                    $f.mobile.nav.clear();

                    if (!has_task) {
                        $f.mobile.nav.show(FormTemplatesPage, {});
                        return;
                    }

                    var task = new MobileTask(this.collection.smetadata.get('task'));

                    try {
                        await this._add_to_local_records($f.mobile.pending_tasks_in_processing, task);
                    } catch (e) {
                        $f.mobile.logging.log_error('Failed to add task to processing queue', {
                            error: e,
                            task: task.toJSON(),
                            rdbms_id: this.collection.metadata.get('rdbms_id')
                        });
                    }

                    $f.mobile.nav.show(TasksPage, {
                        // since we just marked it complete in cache, we don't want to immediately fetch
                        prioritize_cached_results: true
                    });
                };

                $f.mobile.notifications.cancelFutureLocalNotificationForTaskId(
                    this.collection.smetadata.get('task')?.id
                );

                const submitForm = () => {
                    // on web, start upload immediately and remove active collection later so we can reference file objects
                    if (isWeb()) {
                        if (window.IS_FORM_BUILDER_PREVIEW) {
                            $f.mobile.uploads.start_upload({
                                success: function () {
                                    $f.mobile.remove_active_collection(() => {
                                        const event = new Event('saved_from_preview');
                                        window.dispatchEvent(event);
                                    });
                                },
                                error: () => {
                                    this._enable_submit_btn();
                                }
                            });
                        } else {
                            $f.mobile.remove_active_collection(async () => {
                                await _proceed();
                                $f.mobile.uploads.start_upload();
                            });
                        }
                        return;
                    }

                    // on native, we want to remove active collection immediately and upload:
                    $f.mobile.remove_active_collection(async () => {
                        await _proceed();
                        $f.mobile.uploads.start_upload();
                    });
                };

                const submitFormAndFollowUpActions = async (follow_up_actions) => {
                    // This method is for returning the images list just with the s3 keys
                    const cleanedFollowUpActions = cleanUpSMetadata(simplifyFollowUpActionImages(follow_up_actions));
                    this.collection.smetadata.set('follow_up_actions', cleanedFollowUpActions);

                    await this.addFollowUpActionsToSubmissionRecord(this.collection);
                    $f.mobile.nav.go_back();
                    submitForm();
                };

                const simplifyFollowUpActionImages = (actions) =>
                    actions.map(({ images, ...action }) => ({
                        ...action,
                        images: images?.map((image) => image.s3_key) ?? []
                    }));

                const cleanUpSMetadata = (actions) =>
                    actions.map(({ location: { time_zone, ...otherLocationProps }, ...action }) => ({
                        ...action,
                        location: { ...otherLocationProps }
                    }));

                const saveFollowUpActionsOnBack = async (follow_up_actions) => {
                    this.collection.smetadata.set('follow_up_actions', follow_up_actions);
                    $f.mobile.nav.go_back();
                };

                const getInitialFollowUpActions = () => {
                    const initialFollowUpActions = this.collection.smetadata.get('follow_up_actions');
                    return initialFollowUpActions || [];
                };

                const allowFollowUpActionsOnThisForm = this.collection.metadata.get('allow_follow_up_actions');
                const allowAccessToReportingLocations = this.collection.metadata.get('allow_auditors_access_locations');
                if (allowFollowUpActionsOnThisForm) {
                    const selectedLocation = this.getSelectedLocation();

                    /**
                     * @type {import('../../../react/features/FollowUpActions/FollowUpActions.types').MobileFormContext}
                     */
                    const MobileFormContext = {
                        uploadFilesToS3: (files) =>
                            uploadFollowUpActionsFiles(files, this.collection.smetadata.get('guid')),
                        allowAccessToReportingLocations: allowAccessToReportingLocations,
                        allowFollowers: $f.flags.get_flag('follow_up_actions_allow_followers'),
                        formReplyEnabled: $f.flags.get_flag('follow_up_actions_form_reply'),
                        getS3URL: (value) => $f.storage.get_storage_full_url(value),
                        isAdmin: $f.active_user.is_admin()
                    };

                    // Move on to adding follow up actions
                    $f.mobile.nav.show(ReactPageWrapper, {
                        renderFunction: () => (
                            <BackboneProvider bridge={MobileFormContext}>
                                <FollowUpActions
                                    initialFollowUpActions={getInitialFollowUpActions()}
                                    onGoBack={(followUpActions) => saveFollowUpActionsOnBack(followUpActions)}
                                    onSubmit={(followUpActions) => submitFormAndFollowUpActions(followUpActions)}
                                    selectedLocation={selectedLocation}
                                />
                            </BackboneProvider>
                        )
                    });
                } else {
                    submitForm();
                }
            },
            () => {
                $f.mobile.logging.log_error('Failed to add_record() for $f.mobile.pending_form_submissions');
                showErrorMessage();
                this._enable_submit_btn();
            }
        );
    },

    getSelectedLocation: function () {
        let activity = null;
        try {
            let location = this.collection.smetadata.get('account');
            activity = this.collection.metadata.get('activity');
            if (!location) {
                const accountModel = this.collection.findWhere({ type: 'account' });
                location = accountModel ? accountModel.get('account') : null;
            }

            return location ? { id: location.id, label: location.name, time_zone: location.time_zone ?? '' } : null;
        } catch (err) {
            $f.mobile.logging.log_error('Failed to select a location to pass to follow up action', {
                error: err,
                activity_id: activity ? activity.id : null
            });
            return null;
        }
    },

    createFollowUpAction: function () {
        const selectedLocation = this.getSelectedLocation();
        const allowAccessToReportingLocations = this.collection.metadata.get('allow_auditors_access_locations');

        const submitFollowUpActions = (followUpActions) => {
            showLoadingDialog();
            new FollowUpAction().createMultipleFollowUpActions(
                followUpActions,
                'submission',
                this.collection._id.$oid,
                () => {
                    hideLoadingDialog();
                    $f.mobile.nav.go_back();
                    showSuccessMessage(_t('mobile_message_show_success_message'));
                },
                () => {
                    hideLoadingDialog();
                    showErrorMessage();
                }
            );
        };

        const onGoBack = (followUpActions) => {
            if (followUpActions.length > 0) {
                confirmNotification(
                    _t('mobile_mobileform_follow_up_actions_leave_description'),
                    function (success) {
                        if (!success) {
                            $f.mobile.nav.go_back();
                            return;
                        }
                    },
                    _t('mobile_dialog_alert_title'),
                    _t('mobile_mobileform_follow_up_actions_submission_dialog_stay'),
                    _t('mobile_mobileform_follow_up_actions_leave')
                );
            } else {
                $f.mobile.nav.go_back();
            }
        };

        /**
         * @type {import('../../../react/features/FollowUpActions/FollowUpActions.types').MobileFormContext}
         */
        const MobileFormContext = {
            allowAccessToReportingLocations: allowAccessToReportingLocations,
            allowFollowers: $f.flags.get_flag('follow_up_actions_allow_followers'),
            formReplyEnabled: $f.flags.get_flag('follow_up_actions_form_reply'),
            getS3URL: (value) => $f.storage.get_storage_full_url(value),
            uploadFilesToS3: (files) => uploadFollowUpActionsFiles(files, this.collection.smetadata.get('guid')),
            isAdmin: $f.active_user.is_admin()
        };

        $f.mobile.nav.show(ReactPageWrapper, {
            renderFunction: () => (
                <BackboneProvider bridge={MobileFormContext}>
                    <FollowUpActions
                        initialFollowUpActions={[]}
                        pageContext="submission"
                        onGoBack={onGoBack}
                        onSubmit={submitFollowUpActions}
                        selectedLocation={selectedLocation}
                    />
                </BackboneProvider>
            )
        });
    },

    _disable_submit_btn: function () {
        this.$el.find('#submit_form').html(_t('mobile_html_submitting')).attr('disabled', 'disabled');
    },

    _enable_submit_btn: function () {
        this.$el.find('#submit_form').removeAttr('disabled').html(_t('mobile_common_button_submit'));
    },

    click_error_wizard_button: function (event) {
        var target_id = null;

        if ($(event.target).hasClass('next')) {
            target_id = this._field_map.get_next_invalid_id(this._last_visited_model_id);
        } else if ($(event.target).hasClass('prev')) {
            target_id = this._field_map.get_prev_invalid_id(this._last_visited_model_id);
        }

        if (target_id === null) {
            return this.show_error_wizard_buttons();
        }

        this._last_visited_model_id = target_id;
        this.scroll_to_field(target_id);
        this.show_error_wizard_buttons();

        return false;
    },

    show_error_wizard_buttons: function () {
        var $next = this.$el.find('.next');
        var $prev = this.$el.find('.prev');

        var next_id = this._field_map.get_next_invalid_id(this._last_visited_model_id);
        var prev_id = this._field_map.get_prev_invalid_id(this._last_visited_model_id);

        $next.css('visibility', next_id === null ? 'hidden' : 'visible');
        $prev.css('visibility', prev_id === null ? 'hidden' : 'visible');
    },

    hide_error_wizard: function () {
        this.$el.find('.next_prev').hide();

        $f.mobile.nav.add_options({
            _is_wizard_open: false
        });
    },

    /**
     * Check for errors and, if necessary, show the error wizard that will help users step
     * from error to error and see how many are left.
     *
     * @param  {object}     options                 Settings to tweak behavior
     * @param  {boolean}    options.is_resuming     If true, will not scroll to first invalid field.
     *
     * @return {boolean}    False if there are invalid fields, true if not.
     */
    show_error_wizard: function (options) {
        options = options || {};
        var this_ = this;

        this._field_map = this.collection.field_map;

        // with true, this will re-check everything, which will highliglht eveything red with the wizard open
        this._total_invalid = this.collection.get_total_invalid(true);

        if (!this._total_invalid) {
            this.hide_error_wizard();
            return true;
        }

        this._last_visited_model_id = this.collection.get_first_invalid().id;

        this.$el.find('.next_prev_text').text(_t('mobile_forms_required_remaining', { count: this._total_invalid }));
        this.$el.find('.next_prev').show();

        if (!options.is_resuming) {
            this.r_fields.currentView.expand_invalid_sections(function () {
                this_.scroll_to_field(this_._last_visited_model_id);
                showErrorMessage(_t('mobile_message_error_fill_require_fields'));
            });
        }

        this.show_error_wizard_buttons();
        this._is_wizard_open = true;

        $f.mobile.nav.add_options({
            _is_wizard_open: true
        });

        return false;
    },

    _show_history: function () {
        // If there is exactly one account field on the form, pass its value to the submission history list
        var account_fields = this.collection.where({ type: 'account' });
        var search_query =
            account_fields.length == 1 && account_fields[0].get('account') ? account_fields[0].get('account').name : '';

        $f.mobile.nav.show(FormSubmissionsHistoryList, {
            use_transitions: true,
            form_structure: this.collection,
            rdbms_id: this.collection.metadata.get('rdbms_id'),
            toolbarnav_title: this.toolbarnav_title,
            search_query: search_query
        });
    },

    update_error_wizard: function (changed_model) {
        // prevent *programmatically* expanded sections from taking over the last visited id
        if (_.contains(['section', 'formula'], changed_model.get('type'))) {
            return;
        }

        this._last_visited_model_id = changed_model.id;
        this._total_invalid = this.collection.get_total_invalid(false);

        if (!this._total_invalid) {
            this.hide_error_wizard();
            return;
        }

        this.$el.find('.next_prev_text').text(_t('mobile_forms_required_remaining', { count: this._total_invalid }));

        this.show_error_wizard_buttons();
    },

    /**
     * Scroll to a field. If it's in a collapsed section, the section will be expanded.
     */
    scroll_to_field: function (field_id, speed, offset) {
        offset = offset || -50;

        var $el = this.$el.find(`[id=field_${field_id}]`);

        var _scroll = function () {
            $f.application.vent.trigger('mobile_form:scroll_to_element', {
                element: $el,
                speed: speed,
                offset: offset
            });
        };

        if ($el.parents('.form_section.collapsed').length) {
            // auto expand section if necessary
            // get the section for this field and expand it
            this.r_fields.currentView._section_views.forEach(function (section_view) {
                section_view.children_views.forEach(function (field_view) {
                    if (field_view.model.id === field_id) {
                        section_view.expand(_scroll);
                    }
                });
            });
            return;
        }

        _scroll();
    },

    /**
     * Check for running stopwatches.
     *
     * @return {boolean|model} The first running stopwatch, if any. Otherwise, false.
     */
    get_running_stopwatches: function () {
        return this.collection.filter(function (model) {
            if (model.get('type') === 'stopwatch' && !!model.start_date) {
                return true;
            }

            return false;
        });
    },

    _get_current_section_el: function () {
        var current_scroll = this.$el.find('.page_scrollable').scrollTop();
        var $section_headers = this.$el.find('.section_field:visible');

        var $current_section = null;
        $section_headers.each(function (i, el) {
            var $el = $(el);
            // for some reason different offsets are needed for iOS vs Android
            if (el.offsetTop - (isIos() ? 140 : 80) > current_scroll) {
                return true;
            }

            $current_section = $el;
        });

        return $current_section;
    },

    /**
     * Perform all form field events manually. This will happen automatically when the models are changed,
     * but we need to invoke them manually on form render so they reflect an accurate initial state.
     */
    _perform_form_field_events: function () {
        this.collection.each(function (model) {
            $.each(model.get('events'), function (i, event) {
                model.perform_event(event);
            });
        });

        // hidden fields may have been shown, so we should tell our sections to recalculate their section stats
        this.r_fields.currentView &&
            this.r_fields.currentView._section_views.forEach(function (view) {
                view._update_footer();
            });

        if (this._is_wizard_open) {
            this.show_error_wizard({ is_resuming: true }); // don't want to scroll up here, since we're resuming
        }
    },

    /**
     * Count all temperature related fields in the collection.
     */
    _count_temperature_fields: function () {
        return this.collection.filter(
            (model) =>
                model.get('type') == 'temperature' ||
                model.get('type') == 'ingredient_temperature' ||
                model.get('type') == 'equipment_temperature' ||
                model.get('type') == 'sensor_temperature' ||
                model.get('type') == 'oil_quality'
        ).length;
    },

    /**
     * If any BlueTherm fields are in this form, try to connect to the device. If not successful, we'll
     * fail silently and wait for them to retry manually.
     */
    _setup_bluetooth: function () {
        if (!$f.mobile.bluetooth.supports_bluetooth()) {
            return;
        }
        if (isWeb() || this.collection.is_submitted()) {
            return;
        }
        if (this._count_temperature_fields() == 0) {
            return;
        }
        if (this._already_setup_bloothtooth) {
            return;
        } // don't re-set

        this._has_temperature_field = true;
        this._already_setup_bluetooth = true;
        var this_ = this;

        $f.mobile.bluetooth.register_callbacks({
            on_pre_connect: function () {},

            on_device_connected: function () {
                // make sure the user hasn't left the page since starting bluetherm
                if (this_.isClosed) {
                    $f.mobile.bluetooth.disconnect();
                    return;
                }

                showSuccessMessage(_t('mobile_message_blue_therm_success'));
            },

            on_device_disconnected: function (error_code) {
                if (this_.isClosed) {
                    return;
                }
                showErrorMessage(_t('mobile_message_blue_therm_error'));
            },

            on_device_connection_failure: function (error_code) {
                const errMsg = getErrorMessageFromCode(error_code);
                const errPrefix = _t('mobile_message_blue_therm_error_code');

                // always show bluetooth is disabled message
                if (error_code === BluetoothErrorCode.ERROR_BLUETOOTH_DISABLED) {
                    showErrorMessage(`${errPrefix} ${errMsg}`);
                    return;
                }

                showErrorMessage(errMsg);
            },

            on_reading_received: function (value) {},
            on_button_pressed: function (value) {}
        });
    },

    /**
     * Add local record for the draft and start upload to sync this record to the server.
     * @private
     *
     * @param callback (func)
     */
    _sync_draft: function (callback) {
        $f.mobile.form_drafts.add_record(
            this.collection,
            function () {
                toastNotification(_t('mobile_message_drafts_toast_saved'), '', 'df-success', 1500);
                // we want to silently upload drafts.
                $f.mobile.notifications.showUploadSuccessMessage = false;
                $f.mobile.uploads.start_upload({}, false);
                return callback();
            },
            function () {
                showErrorMessage();
            },
            {
                is_synced: false
            }
        );
    },

    /**
     * Logic for confirmation of the draft saving.
     * @private
     *
     * @param collection
     */
    _save_draft_confirmation: function (text, callback) {
        var this_ = this;
        text = text || _t('mobile_message_drafts_confirm');

        if (!isWeb()) {
            var options = {
                title: _t('mobile_forms_leaving_form'),
                subtitle: text,
                position: [50, 60], // only used for iPad
                addCancelButtonWithLabel: _t('mobile_action_sheet_cancel')
            };

            if (isIos()) {
                options.buttonLabels = [_t('mobile_common_button_save_draft')];
                options.addDestructiveButtonWithLabel = _t('mobile_common_button_discard');
                options.destructiveButtonLast = true;
            } else if (isAndroid()) {
                options.buttonLabels = [_t('mobile_common_button_save_draft'), _t('mobile_common_button_discard')];
                options.androidTheme = CordovaPlugins().actionsheet.ANDROID_THEMES.THEME_DEVICE_DEFAULT_LIGHT;
                options.androidEnableCancelButton = true;
            } else if (isWindows()) {
                options.buttonLabels = [_t('mobile_common_button_save_draft'), _t('mobile_common_button_discard')];
            }

            actionSheet(options, function (button_index) {
                if (button_index === 3) {
                    return; // cancel
                }

                if (button_index === 2) {
                    $f.mobile.uploads.clear_pending_file_uploads(this_.collection.smetadata.get('guid'));
                    $f.mobile.pending_form_submissions.remove_records(this_.collection.smetadata.get('guid'));
                    return callback();
                }
                this_._sync_draft(callback);
            });
            return;
        }

        confirmNotification(
            text,
            function (success) {
                if (!success) {
                    $f.mobile.uploads.clear_pending_file_uploads(this_.collection.smetadata.get('guid'));
                    $f.mobile.pending_form_submissions.remove_records(this_.collection.smetadata.get('guid'));
                    return callback();
                }
                this_._sync_draft(callback);
            },
            _t('mobile_forms_leaving_form'),
            _t('mobile_common_button_save_draft'),
            _t('mobile_common_button_discard')
        );
    },

    save_draft: function (callback) {
        var this_ = this;

        // Lets see what state this thing is in
        if (!this.collection.is_started()) {
            return callback();
        }
        if ($f.window.uploading) {
            return callback();
        }

        var running_stopwatches = this.get_running_stopwatches();

        // Loop through the list of running stopwatches and remove them
        var stop_stopwatches = function (cb) {
            cb = cb || function () {};

            var iter_loop = function () {
                if (running_stopwatches.length === 0) {
                    return cb();
                }
                var stopwatch = running_stopwatches.pop();
                this_.r_fields.currentView.children.findByModel(stopwatch).clear_interval();
                $f.mobile.settings.get('stopwatch_start_date_' + stopwatch.id, function (value) {
                    stopwatch.start_date = null;
                    $f.mobile.settings.remove('stopwatch_start_date_' + stopwatch.id, function () {
                        iter_loop();
                    });
                });
            };

            iter_loop();
        };

        /**
         * Passes true if stopwatches are stopped, else false.
         */
        var _confirm_stop_stopwatches = function (cb) {
            cb = cb || function () {};

            if (!running_stopwatches.length) {
                return cb(true);
            }

            logMobileUsage('stopAllStopwatchesPrompted');

            confirmNotification(
                _t('mobile_message_alert_running_stopwatches'),
                function (success) {
                    if (!success) {
                        logMobileUsage('stopAllStopwatchesDeclined');
                        return cb(false);
                    }

                    stop_stopwatches(function () {
                        logMobileUsage('stopAllStopwatchesChosen');
                        cb(true);
                    });
                },
                _t('mobile_field_stopwatch_stop_stopwatches'),
                _t('mobile_common_button_yes'),
                _t('mobile_common_button_no')
            );
        };

        if (this.collection.is_draft()) {
            // this is already a draft
            $f.mobile.form_drafts.get_record(this.collection.smetadata.get('guid'), null, function (draft) {
                // if has not been updated, don't re-save
                if (
                    draft &&
                    draft.smetadata.get('date_modified').$date == this_.collection.smetadata.get('date_modified').$date
                ) {
                    callback();
                    return;
                }

                _confirm_stop_stopwatches(function (stopwatches_stopped) {
                    if (stopwatches_stopped) {
                        this_._save_draft_confirmation(_t('mobile_message_drafts_request_update'), callback);
                    }
                });
            });
        } else {
            _confirm_stop_stopwatches(function (stopwatches_stopped) {
                if (stopwatches_stopped) {
                    this_._save_draft_confirmation(null, callback);
                }
            });
        }
    },

    onClose: function () {
        this.collection.off('change');
        if (!isWeb() && this._has_temperature_field) {
            $f.mobile.bluetooth.unregister_callbacks();
            $f.mobile.bluetooth.disconnect();
        }

        this.$el.find('.page_scrollable').off('scroll.mobile_form');

        if (!this._is_wizard_open) {
            // reset is_valid's so if we come back, validation isn't still in progress.
            this.collection.each(function (model) {
                model.is_valid = null;
            });
        }
    },

    _display_secure_form_header: function () {
        var $lock_icon = $('<i aria-hidden="true" class="nav_icon df-lock"></i>');
        var $text = $(
            '<p> This is a private form. Your submission will only be viewable to those who have access.</p>'
        );
        var $secure_form_header = $('<div class="secure_form_header"> </div>');

        $($secure_form_header).append($($lock_icon));
        $($secure_form_header).append($($text));
        $('.form_outer_container').prepend($($secure_form_header));
    },

    _show_submision_tasks_alerts: function () {
        if (this.expanded_metadata && this.expanded_metadata.rules.length > 0) {
            var rules = new Backbone.Collection();
            rules.add(this.expanded_metadata.rules);

            this.r_submission_tasks_alerts.show(
                new SubmissionsTasksAlertsRulesCard({
                    collection: rules
                })
            );
        }
    }
}); /* end $f.mobile.view.MobileForm */

export const HistoryListItem = Backbone.Marionette.ItemView.extend({
    tagName: 'li',

    events: {
        'click .navlink': 'click_navlink'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    templateHelpers: function () {
        var smetadata = this.model.get('form_structure')
            ? this.model.get('form_structure').smetadata
            : this.model.get('smetadata');
        if (!smetadata) {
            return;
        }

        // Determine what location this submission is for, if possible
        var account_fields = this.model.get('form_structure').where({ type: 'account' });
        var location_data = account_fields.length > 0 ? account_fields[0].get('account') : null;
        var location_name = location_data ? location_data.name : '';
        if (account_fields.length > 1) {
            location_name += ' and more';
        }

        return {
            date: $f.utils.format_submission_date(smetadata, 'ddd, MMM D'),
            time: $f.utils.format_submission_date(smetadata, 'h:mm a z', null, null, true),
            location_name: location_name
        };
    },

    click_navlink: function (event) {
        var this_ = this;

        var submission = new MobileSubmission({ id: this.model.id });
        showLoadingDialog();
        submission.fetch({
            success: function (_submission) {
                hideLoadingDialog();

                var expanded_metadata = submission.get('expanded_metadata');

                // We'll just delete the 'expanded_metadata key so
                // that we're not passing it to the FormStructure constructor
                submission.unset('expanded_metadata');

                $f.mobile.nav.add_options({ collection: this_.model.collection }); // save current collection for when we come back

                $f.mobile.nav.show(SubmittedFormPage, {
                    collection: submission.get('form_structure'),
                    use_transitions: true,
                    expanded_metadata: expanded_metadata
                });
            },
            error: function (error, response) {
                hideLoadingDialog();

                if (response.zenput_code === ZenputCodes.PERMISSION_DENIED) {
                    showErrorMessage(_t('mobile_message_show_submissions_access_denied'));
                    return;
                }

                showErrorMessage();
            }
        });

        return false;
    }
});

export const AllSubmissionsHistoryListItem = HistoryListItem.extend({
    template: '#tpl_submission_history_item'
});

export const FormSubmissionsHistoryListItem = HistoryListItem.extend({
    template: '#tpl_form_history_item',

    templateHelpers: function () {
        var helpers = FormSubmissionsHistoryListItem.__super__.templateHelpers.apply(this, arguments);

        var form_structure = this.model.get('form_structure');
        var created_by = form_structure.smetadata.get('created_by');
        var form_title = created_by && created_by.display_name ? created_by.display_name : '';
        $.extend(helpers, { form_title: form_title });
        return helpers;
    }
});

/**
 * A base class for lists of submission history
 */
export const BaseSubmissionsHistoryList = BaseToolbarNavPage.extend({
    template: '#tpl_history_list',
    itemViewContainer: 'ul.pagelist.history_list',
    className: 'form_template_list submissions_list',

    allow_reverse_transitions: true,
    is_search_running: 0,
    search_criteria_list: null,
    search_criteria: null,
    search_query: '', // here so it can be passed around via our Nav object
    _last_fetch_timestamp: null,

    getEmptyView: function () {
        return EmptyListMessage.extend({
            i18n_key_message: 'mobile_empty_list_message_no_submissions'
        });
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        // If the collection was passed in, we're coming back in time to this view. Rather than re-initializing
        // everything, we can just extract what we set up before from the passed-in-collection.
        if (this.collection) {
            this.search_criteria_list = this.collection.search_criterias;
            this.search_criteria = this.search_criteria_list.at(0);
            return;
        }

        this.search_criteria = new SearchCriteria({ value: this.search_query ? this.search_query : '' });
        this.search_criteria_list = new SearchCriterias([], {
            metadata: {
                next_criteria_id: 1,
                simple_logic: 'and',
                display_result_type: 'list'
            },

            result_type: {
                list: {
                    sort: [{ field_id: 'smetadata.date_submitted', order: -1 }],
                    columns: [],
                    type: 'list'
                }
            }
        });
        this.search_criteria_list.add(this.search_criteria);

        this.collection = new MobileSubmissions([], {
            search_criterias: this.search_criteria_list,
            rdbms_id: this.rdbms_id,
            paginator_options: { output: 'list', data_source: 'elastic_search', view_type: 'simple' }
        });
        this.collection.paginator.set('limit', 25);
    },

    onRender: function () {
        var this_ = this;

        // Show the search bar (can't use regions here, so have to insert manually)
        this.$el
            .find('.search_bar')
            .html('')
            .append(
                new MobileSearchBar({
                    collection: this.collection,
                    default_value: this.search_query,
                    search: function (query, callback) {
                        this_.search(query, callback);
                    }
                }).render().$el
            );

        // no need to reload the collection if we're a historic view, as a filled collection will be provided.
        if (this.collection.length > 0 && this.is_historic_view) {
            return;
        }

        this.$el.find('.empty_list_message_wrapper').hide(); // hide error message until we finish fetching
        showLoadingDialog();

        var fetch_cb = function () {
            hideLoadingDialog();
            this_.$el.find('.empty_list_message_wrapper').show();
        };

        this.collection.fetch({
            success: fetch_cb,
            error: function () {
                if (!isConnected()) {
                    this_.$el.find('.empty_list_message_wrapper p').html(_t('mobile_html_offline_try_again'));
                } else {
                    this_.$el
                        .find('.empty_list_message_wrapper p')
                        .html(_t('mobile_html_something_went_wrong_try_again'));
                }
                fetch_cb();
            }
        });
    },

    onShow: function () {
        BaseSubmissionsHistoryList.__super__.onShow.call(this);
        this._setup_infinite_scroll();
    },

    search: function (query) {
        this.search_criteria.set('value', query);
        $f.mobile.nav.add_options({ search_query: query }); // so the view can keep track for history

        this.collection.paginator.reset_pagination();
        this.collection.fetch({
            reset: true,
            sort: false,
            success: function () {
                hideLoadingDialog();
            },
            error: function () {
                hideLoadingDialog();
            }
        });
    },

    /**
     * after rendering, setup infinite scroll.  note the timeout
     * delay.  without it, the scrollTop wouldnt happen fast enough and start
     * firing requests for the next page
     */
    _setup_infinite_scroll: function () {
        var this_ = this;
        var $pg_scrollable = this.$el.find('.page_scrollable');
        setTimeout(function () {
            if (!this_.is_historic_view) {
                $pg_scrollable.scrollTop(0);
            } // when the page inits, be sure to jump to the top.  inifite scroll will keep going otherwise
            $pg_scrollable.off('scroll');
            $pg_scrollable.on('scroll', function () {
                if ($pg_scrollable.scrollTop() < $pg_scrollable.get(0).scrollHeight - $pg_scrollable.height() - 600) {
                    return;
                }

                // limit fetches to once every 2 seconds
                if (this_._last_fetch_timestamp + 2000 > new Date().getTime()) {
                    return;
                }

                this_._last_fetch_timestamp = new Date().getTime();
                this_.$el.find('.infinite_scroll_loading').css('visibility', 'visible');
                this_.collection.fetch_next({
                    sort: false,
                    success: function () {
                        this_.$el.find('.infinite_scroll_loading').css('visibility', 'hidden');
                    },
                    error: function () {
                        this_.$el.find('.infinite_scroll_loading').css('visibility', 'hidden');
                    },
                    remove: false
                });
            });
        }, 100);
    }
});

/**
 * View to display submission history for a specific template
 *
 *  @requires rdbms_id sent in on initialize
 *  @throws FatalError if rdbms_id is not sent in
 */
export const FormSubmissionsHistoryList = BaseSubmissionsHistoryList.extend({
    itemView: FormSubmissionsHistoryListItem,
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_form_history');
    },

    /**
     * rdbms_id is required to be sent in options
     */
    initialize: function (options) {
        if (!options.rdbms_id) {
            throw new Error('Programmer Error: rdbms_id is missing from FormSubmissionsHistoryList');
        }

        this.rdbms_id = options.rdbms_id;

        FormSubmissionsHistoryList.__super__.initialize.call(this, options);
    }
});

/**
 * This is a list submission history
 */
export const AllSubmissionsHistoryList = BaseSubmissionsHistoryList.extend({
    itemView: AllSubmissionsHistoryListItem,
    toolbarnav_title: function () {
        return _t('mobile_left_menu_my_submissions');
    },
    is_top_level_page: true,

    initialize: function (options) {
        $f.application.vent.trigger('mobile:set_active_leftmenu_item', 'nav_history');
        AllSubmissionsHistoryList.__super__.initialize.call(this, options);
    }
});

/**
 * A version of MobileForm for displaying a previously submitted form.
 */
export const SubmittedFormPage = MobileFormPage.extend({
    screen_name: 'submitted_form',
    expanded_metadata: null, // Holds rule information used to display alerts and tasks
    comments: null, // Comments
    auto_resize_page_scrollable: false, // our TabbedNav pages will handle this

    events: {
        'click .view_pdf': 'view_pdf',
        'click .share_link': 'share_link'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        SubmittedFormPage.__super__.initialize.apply(this, arguments);

        // override default behavior to show pdf export
        this.toolbarnav_actions = [
            {
                container_class: 'view_pdf',
                icon_class: 'df-history',
                i18n_key_text: 'mobile_view_pdf'
            }
        ];
        if (this.collection.metadata.get('is_secure') === false) {
            this.toolbarnav_actions.push({
                container_class: 'share_link',
                icon_class: 'df-send',
                i18n_key_text: 'mobile_share_link'
            });
        }
    },

    onShow: function () {
        var this_ = this;

        SubmittedFormPage.__super__.onShow.call(this);
        this.$el.addClass('historical_submission');

        // show hidden formula fields
        this.$el.find('.hidden_formula').show();

        // Unlike everything else, textareas are not disabled by CSS (their z-index brings them
        // above our CSS barier) to allow the user to scroll within them. Instead, we just make them
        // disabled:
        this.$el.find('textarea').attr('disabled', 'disabled');
        this.$el.find('textarea').attr('readonly', 'readonly');
        this.$el.find('select').attr('disabled', 'disabled'); // the CSS barier also doesn't protect selects on some devices
        this.$el.find('.not_visible').hide();

        this.fetch_comments({
            submission: this.collection,
            success: async function () {
                hideLoadingDialog();
                await this_.add_comments_tab();
                this_._show_tabbed_layout();
                this_.on_orientation_change();
                this_.trigger_dynamic_form_first_load_event();
            },
            error: async function (_error) {
                await this_.add_comments_tab();
                this_._show_tabbed_layout();
                hideLoadingDialog();
                showErrorMessage(_error && _error.message, null);
            }
        });
    },

    on_orientation_change: function () {
        return SubmittedFormPage.__super__.on_orientation_change.apply(this, arguments);
    },

    click_submit: function (event) {
        // submit button is hidden by CSS, but just for paranoia
        return false;
    },

    go_back: function () {
        if (this.is_comments_tab()) {
            return this.go_back_comments_tab();
        }

        $f.mobile.nav.go_back();
    },

    view_pdf: function (event) {
        var this_ = this;
        event.preventDefault();
        this.collection.get_pdf_url(function (pdf_url) {
            if (!pdf_url) {
                return showSuccessMessage(_t('mobile_no_pdf_generated_message'));
            }
            if (isWeb() || isWindows()) {
                $f.mobile.open_uri(pdf_url, '_blank');
                return;
            }
            // Adding random value to the filename so we don't have local files overlap eachother.
            var download_filename = `${this_.collection.metadata.get('title')}${parseInt(Math.random() * 100000)}.pdf`;
            CordovaPlugins().zencam.viewDocumentRemote(
                {
                    path: pdf_url,
                    original_name: download_filename
                },
                function () {},
                function () {
                    $f.mobile.logging.log_error('S3 pdf location could not be loaded.');
                }
            );
        });
        return false;
    },

    share_link: function (event) {
        event.preventDefault();
        FormStructure.get_share_link(
            this.collection._id.$oid,
            function (link) {
                if (isWeb()) {
                    window.alert(link);
                    return;
                }
                CordovaPlugins().clipboard.copy(link);
                showSuccessMessage(_t('mobile_clipboard_copied_success'));
            },
            function () {
                showErrorMessage();
            }
        );
    }
});

Cocktail.mixin(SubmittedFormPage, CommentsPageLayoutMixin);
